import { SYMBOL_PROVIDER_IDENTITY_CHANGED_CODE } from "@signals/shared";
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";
import { PORTFOLIO_COMPARISON_REASON_LABELS, PORTFOLIO_COMPARISON_REASON_OBJ, isRecord, type TPortfolioComparisonReason } from "@signals/shared";
import { Button, Modal } from "../../_common";
import { useApplyProviderIdentityMapping, usePreviewProviderIdentityMapping, type ProviderIdentityMappingPreview, type ProviderIdentityMappingRequest } from "../../symbol";

export interface ProviderIdentityTarget {
  id: string;
  ticker: string;
  name?: string | undefined;
  historical?: ProviderIdentityMappingRequest["historical"];
}
export interface ProviderIdentityModalProps {
  portfolioId: string;
  target: ProviderIdentityTarget | null;
  onClose: () => void;
  onApplied?: (() => void | Promise<void>) | undefined;
  portfolios?: readonly { id: string; name: string }[] | undefined;
}

const dateLabel = (value: string) => value.split("-").reverse().join(".");
function historicalReason(code: TPortfolioComparisonReason | null | undefined): string {
  if (code === PORTFOLIO_COMPARISON_REASON_OBJ.CURRENCY_MISMATCH) return "Валюта источника не совпадает с валютой бумаги. Подтвердить историю нельзя.";
  if (code === PORTFOLIO_COMPARISON_REASON_OBJ.IDENTITY_CONFLICT) return "Источники расходятся в выпуске бумаги или её тикере и площадке. Подтвердить историю нельзя.";
  if (code === PORTFOLIO_COMPARISON_REASON_OBJ.HISTORY_CONFIRMATION_REQUIRED || !code) return "Не найдено проверенного свидетельства брокера для этой даты и бумаги. Одна введённая дата не подтверждает историю.";
  return PORTFOLIO_COMPARISON_REASON_LABELS[code]["ru-RU"];
}
function historicalApplyError(error: Error | null): string | null {
  if (!error) return null;
  let value: unknown;
  try { value = JSON.parse(error.message); } catch { value = null; }
  const detail = isRecord(value) && isRecord(value["error"]) ? value["error"] : value;
  if (isRecord(detail) && detail["code"] === SYMBOL_PROVIDER_IDENTITY_CHANGED_CODE) return "Данные изменились после проверки. Проверьте источник заново перед подтверждением.";
  return "Не удалось сохранить подтверждение. Проверьте источник заново; неподтверждённые изменения не применены.";
}

export function ProviderIdentityModal({ portfolioId, target, onClose, onApplied, portfolios }: ProviderIdentityModalProps): ReactElement {
  const historical = target?.historical;
  const targetKey = JSON.stringify([portfolioId, target?.id, historical?.targetDate, historical?.accountIds, historical?.evidenceRecordIds]);
  const currentTarget = useRef(targetKey); currentTarget.current = targetKey;
  const requestVersion = useRef(0);
  const [providerCode, setProviderCode] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState<ProviderIdentityMappingPreview | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const previewRequest = usePreviewProviderIdentityMapping();
  const applyMapping = useApplyProviderIdentityMapping(portfolioId);

  useEffect(() => {
    requestVersion.current++;
    setProviderCode(target?.ticker ?? "");
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    setPreview(null); setAccepted(false); setResumeError(null); setResuming(false);
    previewRequest.reset(); applyMapping.reset();
  }, [targetKey]);

  const invalidatePreview = () => {
    requestVersion.current++; setPreview(null); setAccepted(false); setResumeError(null);
    previewRequest.reset(); applyMapping.reset();
  };
  const request = (): ProviderIdentityMappingRequest | null => target ? { portfolioId, symbolId: target.id,
    providerCode: providerCode.trim(), effectiveDate: historical?.targetDate ?? effectiveDate,
    ...(historical ? { historical } : {}) } : null;
  const runPreview = async () => {
    const input = request(); if (!input) return;
    const scope = currentTarget.current; const version = ++requestVersion.current;
    setPreview(null); setAccepted(false); setResumeError(null); applyMapping.reset();
    try {
      const result = await previewRequest.mutateAsync(input);
      if (currentTarget.current === scope && requestVersion.current === version) setPreview(result);
    } catch {
      // The mutation owns the displayed error; a failed request cannot leave an applicable preview.
      if (currentTarget.current === scope && requestVersion.current === version) setPreview(null);
    }
  };
  const handlePreview = (event: FormEvent) => { event.preventDefault(); void runPreview(); };
  const historyHasProof = Boolean(preview?.candidate && preview.candidate.evidence.length > 0 && preview.expectedHistoricalProofFingerprint);
  const canApply = Boolean(preview?.found && preview.canApply && (!historical || historyHasProof));
  const alreadyApplied = accepted || preview?.alreadyApplied === true;
  const handleApply = async () => {
    const input = request();
    if (!input || !preview || !canApply || applyMapping.error || resuming) return;
    const scope = currentTarget.current;
    setResumeError(null);
    if (!alreadyApplied) {
      try {
        await applyMapping.mutateAsync({ ...input, expectedMarketPriceRevision: preview.expectedMarketPriceRevision,
          expectedIdentityFingerprint: preview.expectedIdentityFingerprint, affectedPortfolioIds: preview.affectedPortfolioIds,
          ...(historical && preview.expectedHistoricalProofFingerprint ? { expectedHistoricalProofFingerprint: preview.expectedHistoricalProofFingerprint } : {}) });
        if (currentTarget.current !== scope) return;
        setAccepted(true);
      } catch {
        // Keep the mutation error and preview for retry, and never resume an unaccepted mapping.
        if (currentTarget.current === scope) setAccepted(false);
        return;
      }
    }
    if (currentTarget.current !== scope) return;
    setResuming(true);
    try {
      await onApplied?.();
      if (currentTarget.current === scope) onClose();
    } catch {
      if (currentTarget.current === scope) setResumeError(historical
        ? "Подтверждение сохранено, но продолжить подготовку не удалось. Повторите продолжение; сохранять подтверждение заново не нужно."
        : "The mapping was saved, but refreshing the view failed. Try again.");
    } finally { if (currentTarget.current === scope) setResuming(false); }
  };

  const affected = preview?.affectedPortfolioIds ?? [];
  const names = new Map(portfolios?.map((portfolio) => [portfolio.id, portfolio.name]));
  const affectedNames = affected.flatMap((id) => names.has(id) ? [names.get(id)!] : id === portfolioId ? ["Текущий портфель"] : []);
  const unknownNames = affected.length - affectedNames.length;
  const mutationError = historical ? historicalApplyError(applyMapping.error) ?? (previewRequest.error
    ? "Не удалось проверить источник. Проверьте доступ к брокерской выписке и повторите проверку." : null)
    : (previewRequest.error ?? applyMapping.error)?.message;
  const busy = previewRequest.isPending || applyMapping.isPending || resuming;
  const candidate = preview?.candidate;

  return (
    <Modal open={target !== null} onClose={onClose} title={historical ? `Подтверждение истории · ${target?.name ?? target?.ticker ?? ""}` : `Correct FMP ticker · ${target?.ticker ?? ""}`}>
      <p className="mb-3 text-sm text-muted-foreground">{historical
        ? "Проверка связывает исторический тикер с этой бумагой по подтверждённой выписке брокера. Сделки, количество и денежные операции не меняются."
        : "This changes the FMP ticker assigned to this security across every portfolio. The portfolio ledger stays unchanged."}</p>
      {historical && <p className="mb-3 text-sm">Дата нужного среза: <strong>{dateLabel(historical.targetDate)}</strong>. Подготовка проверит подходящее закрытие не позднее этой даты.</p>}
      <form onSubmit={handlePreview} className="flex min-w-0 flex-col gap-3">
        <label className="text-sm font-medium" htmlFor="fmp-provider-code">{historical ? "Тикер в FMP" : "FMP ticker"}</label>
        <input id="fmp-provider-code" className="h-9 min-w-0 rounded-md border border-input bg-background px-3 text-sm" autoFocus
          required value={providerCode} disabled={applyMapping.isPending || resuming}
          onChange={(event) => { setProviderCode(event.target.value); invalidatePreview(); }} />
        {!historical && <>
          <label className="text-sm font-medium" htmlFor="fmp-effective-date">Use this ticker from</label>
          <input id="fmp-effective-date" className="h-9 rounded-md border border-input bg-background px-3 text-sm" type="date" required
            value={effectiveDate} disabled={applyMapping.isPending || resuming}
            onChange={(event) => { setEffectiveDate(event.target.value); invalidatePreview(); }} />
        </>}
        {(mutationError || resumeError) && <p role="alert" className="text-sm text-destructive">{resumeError ?? mutationError}</p>}
        {preview && <section className="min-w-0 rounded-md border p-3 text-sm" aria-live="polite" aria-label={historical ? "Результат проверки исторического источника" : "FMP record"}>
          {historical ? <>
            <h3 className="font-medium">Проверенное свидетельство брокера</h3>
            {candidate && <>
              <p>Фактический день свидетельства: <strong>{dateLabel(candidate.coveredFrom)}</strong>.</p>
              <p>{candidate.providerCode} · {candidate.exchange} · {candidate.currency}</p>
              <p>Подтверждён только день {dateLabel(candidate.coveredFrom)}. Начиная с {dateLabel(candidate.coveredTo)}, это свидетельство не подтверждает другие дни истории.</p>
              <ul className="mt-2 flex flex-col gap-2" aria-label="Источники подтверждения">
                {candidate.evidence.map((record) => <li key={record.recordId} className="min-w-0 rounded border p-2">
                  <p>Брокерский счёт {record.brokerAccountId} · {dateLabel(record.date)}</p>
                  <p>{record.providerCode} · {record.exchange} · {record.currency}</p>
                  <p>ISIN: {record.isin ?? "не указан"}; CUSIP: {record.cusip ?? "не указан"}</p>
                  <details><summary className="cursor-pointer">Ссылка на запись брокера</summary><p className="break-all">{record.sourceRecordKey}</p></details>
                </li>)}
              </ul>
            </>}
            <p className="mt-2">Затронуто портфелей: {affected.length}.</p>
            {affectedNames.length > 0 && <ul aria-label="Затронутые портфели">{affectedNames.map((name, index) => <li key={`${name}:${index}`}>{name}</li>)}</ul>}
            {unknownNames > 0 && <p>Ещё {unknownNames} портфелей; названия недоступны.</p>}
            {alreadyApplied && <p>Это свидетельство уже принято. Можно продолжить ту же подготовку без повторного сохранения.</p>}
            {!canApply && <p role="alert" className="mt-2 text-destructive">{historicalReason(preview.reasonCode)}</p>}
          </> : <>
            <h3 className="font-medium">FMP record</h3>
            {preview.provider && <>
              <p>{preview.provider.name ?? preview.provider.code} · {preview.provider.exchange ?? "exchange unavailable"} · {preview.provider.currency ?? "currency unavailable"}</p>
              <p>ISIN: {preview.provider.isin ?? "not provided"}; CUSIP: {preview.provider.cusip ?? "not provided"}</p>
            </>}
            <p>Also affects {Math.max(0, affected.length - 1)} other portfolio(s).</p>
            {alreadyApplied && <p>This ticker is already assigned from this date.</p>}
            {!preview.canApply && <p role="alert" className="text-destructive">Cannot confirm this ticker: {preview.reason ?? "FMP identifiers did not match"}.</p>}
          </>}
        </section>}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{historical ? "Отмена" : "Cancel"}</Button>
          {!preview && <Button type="submit" disabled={busy}>{previewRequest.isPending ? historical ? "Проверяем источник…" : "Checking FMP..." : historical ? "Проверить источник" : "Check ticker"}</Button>}
          {preview && <>
            <Button type="button" variant="outline" disabled={busy} onClick={() => { void runPreview(); }}>{historical ? "Проверить заново" : "Check again"}</Button>
            <Button type="button" disabled={!canApply || Boolean(applyMapping.error) || busy || (!historical && alreadyApplied && !onApplied)} onClick={() => { void handleApply(); }}>
              {applyMapping.isPending ? historical ? "Сохраняем…" : "Applying..." : resuming ? historical ? "Продолжаем…" : "Refreshing..."
                : historical ? alreadyApplied ? "Продолжить подготовку" : "Подтвердить и продолжить" : alreadyApplied ? "Already applied" : "Apply ticker"}
            </Button>
          </>}
        </div>
      </form>
    </Modal>
  );
}
